import { createContext, useContext, type Context } from "react";

import type { I18n, Messages } from "@lingui/core";
import { compileMessage } from "@lingui/message-utils/compileMessage";
import { useLingui } from "@lingui/react";
import { basename } from "@mobsuccess-devops/react-node/path";

import Cookies from "js-cookie";

import { isMtEditEnabled } from "./mt-edit-enabled";

export const ENV_LOCALES: string[] = (import.meta.env.VITE_LANGUAGES ?? "en,fr")
  .split(",")
  .map((locale: string) => locale.trim())
  .filter(Boolean);

const sharedLocales = import.meta.glob<LocaleModule>(
  ["../../locales/*.json", "!../../locales/messages.json"],
  { eager: true, query: "?lingui" },
);

// @ts-expect-error FTTECH-1891
const fallbackLocale = basename(Object.keys(sharedLocales)[0], ".json");

export type LocaleModule = {
  messages: Messages;
};

export type LanguageContextType = {
  language: string;
  /** Locale requested at boot (cookie, browser) that had no catalog, when `language` is a fallback */
  fallbackFrom?: string;
  languagesMap: Record<string, string>;
  availableLanguages: string[];
  onLanguageChange: (language: string) => Promise<void>;
};

declare global {
  // HMR-safe singleton: Vite may re-evaluate this module in dev, which
  // would create a second LanguageContext and desync provider/consumer.
  // Caching on globalThis guarantees one context object across reloads.
  // eslint-disable-next-line no-var
  var __msLanguageContext: Context<LanguageContextType | null> | undefined;
}

export const LanguageContext: Context<LanguageContextType | null> =
  (globalThis.__msLanguageContext ??= createContext<LanguageContextType | null>(
    null,
  ));

export function useLanguageContext(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("LanguageContext is not defined");
  }
  return context;
}

export function useI18n(): I18n {
  const { i18n } = useLingui();
  return i18n;
}

// Which Mobtranslate project owns which compiled catalog, for the in-app edit
// mode: it needs that ownership to know where to write, and the source locale
// of a distribution only carries the strings corrected in Mobtranslate. Holds
// references to the catalog modules, never copies of them.
const compiledByProject: Map<
  string,
  Map<string, LocaleModule["messages"][]>
> = new Map();

function rememberCompiled(
  projectId: string,
  locale: string,
  messages: LocaleModule["messages"],
): void {
  const byLocale = compiledByProject.get(projectId) ?? new Map();
  byLocale.set(locale, [...(byLocale.get(locale) ?? []), messages]);
  compiledByProject.set(projectId, byLocale);
}

export function buildLocales(
  webappsLocales: Record<string, LocaleModule>,
): Map<string, LocaleModule["messages"]> {
  const projectId = import.meta.env.VITE_MOON_PROJECT_ID;
  const allLocales: [string, LocaleModule, string][] = [
    ...Object.entries(sharedLocales).map(
      ([key, module]): [string, LocaleModule, string] => [
        key,
        module,
        "react-shared",
      ],
    ),
    ...Object.entries(webappsLocales).map(
      ([key, module]): [string, LocaleModule, string] => [
        key,
        module,
        projectId,
      ],
    ),
  ];
  return allLocales.reduce((locales, [key, module, ownerProjectId]) => {
    const locale = basename(key).replace(/\.(ts|json)$/, "");
    if (!locales.has(locale)) {
      locales.set(locale, {});
    }
    const loaded = locales.get(locale);
    locales.set(locale, {
      ...loaded,
      ...module.messages,
    });
    if (ownerProjectId) {
      rememberCompiled(ownerProjectId, locale, module.messages);
    }
    return locales;
  }, new Map<string, LocaleModule["messages"]>());
}

// Mobtranslate public distributions, one per project, shaped
// { locale: { key: ICU text } } with app locale codes (no mapping needed).
const mobtranslateUrl =
  import.meta.env.VITE_MOBTRANSLATE_URL ?? "https://mobtranslate.mobsuccess.ai";
const failedDistantLocales: Set<string> = new Set();
const distantLocales: Map<string, LocaleModule["messages"]> = new Map();
type DistributionBundle = Record<string, InputMessages>;
const distantBundles: Map<string, Promise<DistributionBundle>> = new Map();

// a slow Mobtranslate must not block boot: past BOOT_WAIT_MS the app falls
// back to the compiled catalogs while the bundle download (bounded by
// BUNDLE_TIMEOUT_MS, multi-MB on slow links) finishes in the background and
// hot-swaps on arrival
const POINTER_TIMEOUT_MS = 5000;
const BUNDLE_TIMEOUT_MS = 60_000;
const BOOT_WAIT_MS = 5000;

async function fetchJson<T>(
  url: string,
  timeoutMs: number = POINTER_TIMEOUT_MS,
): Promise<T> {
  const res = await fetch(url, {
    // oxlint-disable-next-line mobsuccess/member-expressions
    signal: AbortSignal.timeout(timeoutMs),
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch ${url}: HTTP ${res.status}`);
  }
  return res.json();
}

// defaults to the latest published release; VITE_USE_MOBTRANSLATE_DEV=true
// opts into the "dev" distribution (current project state)
const useDevDistribution = import.meta.env.VITE_USE_MOBTRANSLATE_DEV === "true";

const distantDisabledInTests =
  import.meta.env.MS_VITEST === "true" &&
  import.meta.env.VITE_MOBTRANSLATE_TEST !== "true";

// Bundle cache, stale-while-revalidate: boot from localStorage, check
// /latest in the background, hot-swap via i18n.load() when the hash moved.
type VersionedBundle = { version: string; content: DistributionBundle };

const bundleStorageBase = "mobtranslate:";
const bundleStoragePrefix = `${bundleStorageBase}v1:${useDevDistribution ? "dev" : "release"}:`;

export function clearStoredBundles(): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(bundleStorageBase)) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // storage unavailable
  }
}

function isVersionedBundle(value: unknown): value is VersionedBundle {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  if (!("version" in value) || typeof value.version !== "string") {
    return false;
  }
  return (
    "content" in value &&
    typeof value.content === "object" &&
    value.content !== null
  );
}

function readStoredBundle(projectId: string): VersionedBundle | undefined {
  try {
    const raw = localStorage.getItem(bundleStoragePrefix + projectId);
    if (!raw) {
      return undefined;
    }
    const parsed: unknown = JSON.parse(raw);
    return isVersionedBundle(parsed) ? parsed : undefined;
  } catch {
    // localStorage unavailable (SSR, private mode) or corrupted JSON
    return undefined;
  }
}

let storageWriteFailureReported = false;

function writeStoredBundle(projectId: string, bundle: VersionedBundle): void {
  try {
    // only persist the locales the app can display: the full multi-locale
    // bundle can weigh MBs and localStorage quota is shared origin-wide
    const content = Object.fromEntries(
      Object.entries(bundle.content).filter(([locale]) =>
        ENV_LOCALES.includes(locale),
      ),
    );
    localStorage.setItem(
      bundleStoragePrefix + projectId,
      JSON.stringify({ version: bundle.version, content }),
    );
  } catch (e) {
    // quota exceeded or storage unavailable: the cache is best-effort
    if (!storageWriteFailureReported) {
      storageWriteFailureReported = true;
      void reportException(e);
    }
  }
}

// With knownVersion: returns undefined when the hash is unchanged (nothing
// gets downloaded).
function fetchRemoteBundle(projectId: string): Promise<VersionedBundle>;
function fetchRemoteBundle(
  projectId: string,
  knownVersion: string,
): Promise<VersionedBundle | undefined>;
async function fetchRemoteBundle(
  projectId: string,
  knownVersion?: string,
): Promise<VersionedBundle | undefined> {
  if (useDevDistribution) {
    const { content } = await fetchJson<{ content: DistributionBundle }>(
      `${mobtranslateUrl}/api/v1/projects/${projectId}/distributions/dev`,
      BUNDLE_TIMEOUT_MS,
    );
    return { version: "dev", content };
  }
  const { url } = await fetchJson<{ url: string }>(
    `${mobtranslateUrl}/api/v1/projects/${projectId}/distributions/latest`,
  );
  if (knownVersion !== undefined && url === knownVersion) {
    return undefined;
  }
  const { content } = await fetchJson<{ content: DistributionBundle }>(
    `${mobtranslateUrl}${url}`,
    BUNDLE_TIMEOUT_MS,
  );
  return { version: url, content };
}

async function persistFreshBundle(projectId: string): Promise<VersionedBundle> {
  const fresh = await fetchRemoteBundle(projectId);
  writeStoredBundle(projectId, fresh);
  return fresh;
}

async function applyLateBundle(
  projectId: string,
  freshPromise: Promise<VersionedBundle>,
): Promise<void> {
  try {
    const late = await freshPromise;
    await applyRefreshedBundle(projectId, late.content);
  } catch (e) {
    await reportException(e);
  }
}

function raceBootWait(
  freshPromise: Promise<VersionedBundle>,
): Promise<VersionedBundle | undefined> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => resolve(undefined), BOOT_WAIT_MS);
    void (async () => {
      try {
        const value = await freshPromise;
        clearTimeout(timer);
        resolve(value);
      } catch (error) {
        clearTimeout(timer);
        reject(error);
      }
    })();
  });
}

async function loadDistributionBundle(
  projectId: string,
): Promise<DistributionBundle> {
  const stored = readStoredBundle(projectId);
  if (stored) {
    void refreshDistributionBundle(projectId, stored.version);
    return stored.content;
  }
  const freshPromise = persistFreshBundle(projectId);
  const fresh = await raceBootWait(freshPromise);
  if (fresh) {
    return fresh.content;
  }
  // slow download: boot on the compiled catalogs, hot-swap on arrival
  void applyLateBundle(projectId, freshPromise);
  const timeout = new Error(`Timed out fetching the ${projectId} bundle`);
  timeout.name = BOOT_TIMEOUT_ERROR_NAME;
  throw timeout;
}

async function refreshDistributionBundle(
  projectId: string,
  knownVersion: string,
): Promise<void> {
  try {
    const fresh = await fetchRemoteBundle(projectId, knownVersion);
    if (!fresh) {
      return; // same hash: the local cache is up to date
    }
    writeStoredBundle(projectId, fresh);
    await applyRefreshedBundle(projectId, fresh.content);
  } catch (e) {
    // best-effort: the current cache keeps being served, retried next boot
    await reportException(e);
  }
}

async function applyRefreshedBundle(
  projectId: string,
  content: DistributionBundle,
): Promise<void> {
  distantBundles.set(projectId, Promise.resolve(content));
  for (const key of Array.from(distantLocales.keys())) {
    if (key.startsWith(`${projectId}_`)) {
      distantLocales.delete(key);
    }
  }
  for (const key of Array.from(failedDistantLocales)) {
    if (key.startsWith(`${projectId}_`)) {
      failedDistantLocales.delete(key);
    }
  }
  if (!activeCatalogContext) {
    return;
  }
  const { i18n, locale, locales } = activeCatalogContext;
  const catalog = await buildCatalog(locale, locales);
  if (catalog) {
    i18n.load(locale, catalog);
  }
}

async function fetchDistantLocale(
  projectId: string,
  locale: string,
  { cacheOnly }: { cacheOnly: boolean },
): Promise<Messages | undefined> {
  if (distantDisabledInTests) {
    return undefined;
  }
  const cacheKey = `${projectId}_${locale}`;
  if (distantLocales.has(cacheKey)) {
    return distantLocales.get(cacheKey);
  }
  if (failedDistantLocales.has(cacheKey)) {
    return undefined;
  }
  let bundlePromise = distantBundles.get(projectId);
  if (!bundlePromise) {
    if (cacheOnly) {
      failedDistantLocales.add(cacheKey);
      return undefined;
    }
    bundlePromise = loadDistributionBundle(projectId);
    distantBundles.set(projectId, bundlePromise);
  }
  try {
    const bundle = await bundlePromise;
    const messages = bundle[locale];
    if (!messages) {
      // a published release missing a locale would otherwise go unnoticed
      await reportException(
        new Error(`Locale ${locale} missing from the ${projectId} bundle`),
      );
      failedDistantLocales.add(cacheKey);
      return undefined;
    }
    // edit mode: prefix each string with an invisible key marker (mt-edit.ts)
    let raw = messages;
    if (typeof window !== "undefined" && isMtEditEnabled()) {
      try {
        const { wrapWithMarkers } = await import("./mt-edit");
        raw = wrapWithMarkers(projectId, messages);
      } catch {
        // best-effort: text matching takes over without markers
      }
    }
    const compiledMessages = compileLinguiDynamicMessages(raw);
    distantLocales.set(cacheKey, compiledMessages);
    return compiledMessages;
  } catch (e) {
    // evict the rejected promise so a later call can retry
    if (distantBundles.get(projectId) === bundlePromise) {
      distantBundles.delete(projectId);
    }
    // we don't want to block the app if the distant locale is not available, but we want to know about it
    await reportException(e);
    failedDistantLocales.add(cacheKey);
    return undefined;
  }
}

function isTransientNetworkError(error: unknown): boolean {
  if (error instanceof DOMException) {
    return error.name === "AbortError" || error.name === "TimeoutError";
  }
  if (error instanceof Error && error.name === BOOT_TIMEOUT_ERROR_NAME) {
    return true;
  }
  return (
    error instanceof TypeError &&
    /failed to fetch|load failed|networkerror/i.test(error.message)
  );
}

const BOOT_TIMEOUT_ERROR_NAME = "MobtranslateBootTimeout";

// navigating away kills in-flight fetches; only those failures are dropped
let pageUnloading = false;
function markPageUnloading(): void {
  pageUnloading = true;
  setTimeout(() => {
    pageUnloading = false;
  }, 10_000);
}
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", markPageUnloading);
  window.addEventListener("pagehide", markPageUnloading);
}

async function reportException(error: unknown): Promise<void> {
  if (pageUnloading && isTransientNetworkError(error)) {
    return;
  }
  try {
    const { captureException } = await import("@sentry/react");
    captureException(error);
  } catch {
    // Sentry failed to load — reporting is best-effort
  }
}

// Set by activate(), needed to hot-swap bundles refreshed in the background
// (see applyRefreshedBundle).
type ActiveCatalogContext = {
  i18n: I18n;
  locale: string;
  locales: Map<string, LocaleModule["messages"]>;
};
let activeCatalogContext: ActiveCatalogContext | undefined;

let bootFallbackFrom: string | undefined;

export function getBootFallbackFrom(): string | undefined {
  return bootFallbackFrom;
}

async function buildCatalog(
  resolvedLocale: string,
  locales: Map<string, LocaleModule["messages"]>,
  options: { cacheOnly: boolean } = { cacheOnly: false },
): Promise<Messages | undefined> {
  const projectId = import.meta.env.VITE_MOON_PROJECT_ID;
  const [distantReactSharedLocale, distantLocale] = await Promise.all([
    fetchDistantLocale("react-shared", resolvedLocale, options),
    projectId
      ? fetchDistantLocale(projectId, resolvedLocale, options)
      : Promise.resolve(undefined),
  ]);
  const locale = locales.get(resolvedLocale);
  if (!distantLocale && !locale) {
    return undefined;
  }
  return {
    ...locale,
    ...distantReactSharedLocale,
    ...distantLocale,
  };
}

export async function activate(
  i18n: I18n,
  locales: Map<string, LocaleModule["messages"]>,
): Promise<string> {
  const requestedLocale = await resolveAppLocale(null);
  let resolvedLocale = requestedLocale;

  // set before the fetches so an early background refresh can already swap
  activeCatalogContext = { i18n, locale: resolvedLocale, locales };
  let catalog = await buildCatalog(resolvedLocale, locales);
  if (!catalog) {
    const compiledLocale = await resolveAppLocale(
      null,
      compiledLocales(locales),
    );
    if (compiledLocale !== resolvedLocale) {
      resolvedLocale = compiledLocale;
      activeCatalogContext = { i18n, locale: resolvedLocale, locales };
      // a failed or slow bundle is not downloaded twice: applyLateBundle swaps it in
      catalog = await buildCatalog(resolvedLocale, locales, {
        cacheOnly: true,
      });
    }
  }
  if (!catalog) {
    throw new Error(`Language ${resolvedLocale} is not supported`);
  }

  bootFallbackFrom =
    resolvedLocale === requestedLocale ? undefined : requestedLocale;
  i18n.load(resolvedLocale, catalog);
  i18n.activate(resolvedLocale);
  void maybeSetupMtEdit(resolvedLocale);
  return resolvedLocale;
}

/** In-app edit mode (?mt-edit=1), lazy-loaded, reuses the cached bundles. */
async function maybeSetupMtEdit(locale: string): Promise<void> {
  if (typeof document === "undefined") {
    return;
  }
  try {
    if (!isMtEditEnabled()) {
      return;
    }
    const { setupMtEdit, toPlainTextCatalog } = await import("./mt-edit");
    const projectId = import.meta.env.VITE_MOON_PROJECT_ID;
    const ids: string[] = ["react-shared", ...(projectId ? [projectId] : [])];
    const projects = (
      await Promise.all(
        ids.map(async (id) => {
          let distant: DistributionBundle | undefined;
          try {
            distant = await distantBundles.get(id);
          } catch {
            // bundle unavailable: the compiled catalog carries the mode
          }
          // The compiled catalog owns the source locale, the distribution only
          // carries what Mobtranslate overrides: an editable string can come
          // from either side.
          const bundle: Record<string, Record<string, string>> = {};
          for (const [loc, catalogs] of compiledByProject.get(id) ?? []) {
            bundle[loc] = toPlainTextCatalog(Object.assign({}, ...catalogs));
          }
          for (const [loc, messages] of Object.entries(distant ?? {})) {
            bundle[loc] = { ...bundle[loc], ...messages };
          }
          return Object.keys(bundle).length ? { id, bundle } : undefined;
        }),
      )
    ).filter((p): p is NonNullable<typeof p> => Boolean(p));
    if (projects.length) {
      setupMtEdit({
        mobtranslateUrl,
        locale,
        sourceLocale: "fr",
        projects,
      });
    }
  } catch (e) {
    await reportException(e);
  }
}

export async function changeLocale(locale: string): Promise<void> {
  const resolvedLocale = await resolveAppLocale(locale);
  // oxlint-disable-next-line mobsuccess/member-expressions
  Cookies.set("ms_lang", resolvedLocale, {
    domain:
      import.meta.env.VITE_IS_DEV === "true" ? undefined : ".mobsuccess.com",
    expires: 365,
    sameSite: "strict",
    secure: window.location.protocol === "https:",
  });
  window.location.reload();
}

function compiledLocales(
  locales: Map<string, LocaleModule["messages"]>,
): string[] {
  const compiled = ENV_LOCALES.filter((locale) => locales.has(locale));
  return compiled.length > 0 ? compiled : ENV_LOCALES;
}

async function resolveAppLocale(
  locale: string | null = null,
  supportedLocales: string[] = ENV_LOCALES,
): Promise<string> {
  if (isSupportedLocale(locale, supportedLocales)) {
    return locale;
  }

  const cookieLocale = getCookieLocale(supportedLocales);
  if (cookieLocale) {
    return cookieLocale;
  }

  const browserLanguage = getBrowserLanguage(supportedLocales);
  if (browserLanguage) {
    return browserLanguage;
  }

  return getDefaultLocale(supportedLocales, fallbackLocale);
}

function isSupportedLocale(
  locale: string | null,
  supportedLocales: string[],
): locale is string {
  return Boolean(locale && supportedLocales.includes(locale));
}

function getCookieLocale(supportedLocales: string[]): string | null {
  try {
    // oxlint-disable-next-line mobsuccess/member-expressions
    const locale = Cookies.get("ms_lang") ?? null;
    return isSupportedLocale(locale, supportedLocales) ? locale : null;
  } catch {
    return null;
  }
}

function getBrowserLanguage(supportedLocales: string[]): string | null {
  try {
    const [language] = navigator.language.split("-");
    // @ts-expect-error FTTECH-1891
    return isSupportedLocale(language, supportedLocales) ? language : null;
  } catch {
    return null;
  }
}

function getDefaultLocale(
  supportedLocales: string[],
  fallback: string,
): string {
  const defaultLocale = supportedLocales?.[0];
  // @ts-expect-error FTTECH-1891
  return isSupportedLocale(defaultLocale, supportedLocales)
    ? defaultLocale
    : fallback;
}

export type InputMessages = Record<string, string>;

export function compileLinguiDynamicMessages(
  inputMessages: InputMessages,
): Messages {
  return Object.fromEntries(
    Object.entries(inputMessages).map(([key, value]) => {
      return [key, compileMessage(value)];
    }),
  );
}
